import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import ProductCard from "../components/shop/ProductCard";
import SlimAdBanner from "../components/ui/SlimAdBanner";
import { SlidersHorizontal, X, ChevronLeft, ChevronRight, Star, ChevronDown, ChevronUp } from "lucide-react";
import { CATEGORY_GROUPS, CATEGORY_TREE, resolveCategory, formatPrice } from "../utils/format";

const PAGE_SIZE = 12;

/* ─── Filter Sidebar Content ──────────────────────────────────────────────── */
function FilterContent({ searchParams, setParam, clearFilters }) {
  const rawCategory    = searchParams.get("category") || "";
  const rawSubcategory = searchParams.get("subcategory") || "";
  
  // Resolve category/subcategory (handles slugs and direct subcategory names in category param)
  const resolved = resolveCategory(rawCategory);
  const category = resolved.category || rawCategory;
  const subcategory = rawSubcategory || resolved.subcategory;

  const maxPrice  = Number(searchParams.get("maxPrice")) || 0;
  const minRating = Number(searchParams.get("minRating")) || 0;
  const inStock   = searchParams.get("inStock") === "true";

  // Track expanded category in accordion (defaults to active category)
  const [expandedCat, setExpandedCat] = useState(category || "");

  useEffect(() => {
    if (category) setExpandedCat(category);
  }, [category]);

  const Section = ({ title, children }) => (
    <div style={{ borderBottom: "1px solid var(--border)", paddingBottom: 20, marginBottom: 20 }}>
      <p style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--text-muted)", marginBottom: 12 }}>{title}</p>
      {children}
    </div>
  );

  return (
    <div>
      <Section title="Departments & Categories">
        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <button
            onClick={() => { setParam("category", ""); setParam("subcategory", ""); }}
            style={{
              width: "100%", textAlign: "left", padding: "8px 10px", borderRadius: "var(--radius-sm)",
              background: !category ? "var(--brand-light)" : "transparent",
              border: "none", fontSize: 13,
              color: !category ? "var(--brand)" : "var(--text)",
              fontWeight: !category ? 700 : 400,
              cursor: "pointer", transition: "background 0.15s"
            }}
          >
            All Categories
          </button>

          {CATEGORY_GROUPS.map(grp => {
            const isCurrentCat = category.toLowerCase() === grp.group.toLowerCase();
            const isExpanded = expandedCat.toLowerCase() === grp.group.toLowerCase();

            return (
              <div key={grp.group} style={{ marginTop: 4, borderRadius: "var(--radius-sm)", overflow: "hidden" }}>
                <div style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  padding: "7px 10px",
                  borderRadius: "var(--radius-sm)",
                  background: isCurrentCat ? "var(--brand-light)" : "transparent",
                  cursor: "pointer",
                  transition: "background 0.15s",
                }}
                onMouseEnter={e => { if (!isCurrentCat) e.currentTarget.style.background = "var(--surface)"; }}
                onMouseLeave={e => { if (!isCurrentCat) e.currentTarget.style.background = "transparent"; }}
                >
                  <button
                    onClick={() => {
                      setParam("category", grp.group);
                      setParam("subcategory", "");
                      setExpandedCat(grp.group);
                    }}
                    style={{
                      background: "none", border: "none", padding: 0,
                      display: "flex", alignItems: "center", gap: 8,
                      flex: 1, textAlign: "left", cursor: "pointer",
                      fontSize: 13,
                      fontWeight: isCurrentCat ? 700 : 500,
                      color: isCurrentCat ? "var(--brand)" : "var(--text)"
                    }}
                  >
                    <span style={{ fontSize: 15 }}>{grp.icon}</span>
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {grp.group}
                    </span>
                  </button>

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setExpandedCat(isExpanded ? "" : grp.group);
                    }}
                    style={{
                      background: "none", border: "none", padding: "2px 4px",
                      cursor: "pointer", color: "var(--text-muted)", display: "flex",
                      alignItems: "center"
                    }}
                    aria-label="Toggle subcategories"
                  >
                    {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                  </button>
                </div>

                {/* Subcategories */}
                {isExpanded && (
                  <div style={{
                    paddingLeft: 22, paddingTop: 4, paddingBottom: 6,
                    display: "flex", flexDirection: "column", gap: 2,
                    borderLeft: "2px solid rgba(232, 71, 26, 0.2)",
                    marginLeft: 14, marginTop: 2
                  }}>
                    <button
                      onClick={() => {
                        setParam("category", grp.group);
                        setParam("subcategory", "");
                      }}
                      style={{
                        textAlign: "left", padding: "4px 8px", borderRadius: 4,
                        border: "none", fontSize: 12, cursor: "pointer",
                        background: isCurrentCat && !subcategory ? "rgba(232, 71, 26, 0.12)" : "transparent",
                        color: isCurrentCat && !subcategory ? "var(--brand)" : "var(--text-muted)",
                        fontWeight: isCurrentCat && !subcategory ? 700 : 400
                      }}
                    >
                      All {grp.group}
                    </button>

                    {grp.items.map(item => {
                      const isSubActive = isCurrentCat && subcategory.toLowerCase() === item.name.toLowerCase();
                      return (
                        <button
                          key={item.name}
                          onClick={() => {
                            setParam("category", grp.group);
                            setParam("subcategory", item.name);
                          }}
                          style={{
                            textAlign: "left", padding: "5px 8px", borderRadius: 4,
                            border: "none", fontSize: 12, cursor: "pointer",
                            background: isSubActive ? "rgba(232, 71, 26, 0.12)" : "transparent",
                            color: isSubActive ? "var(--brand)" : "var(--text)",
                            fontWeight: isSubActive ? 700 : 400,
                            display: "flex", alignItems: "center", gap: 6,
                            transition: "background 0.15s"
                          }}
                          onMouseEnter={e => { if (!isSubActive) e.currentTarget.style.background = "var(--surface)"; }}
                          onMouseLeave={e => { if (!isSubActive) e.currentTarget.style.background = "transparent"; }}
                        >
                          <span style={{ fontSize: 13 }}>{item.icon}</span>
                          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {item.name}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Section>

      <Section title="Max Price">
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {[[0, "Any Price"], [50000, "Under ₦50,000"], [100000, "Under ₦100,000"], [250000, "Under ₦250,000"], [500000, "Under ₦500,000"], [1000000, "Under ₦1M"]].map(([v, label]) => (
            <button key={v} onClick={() => setParam("maxPrice", v || "")}
              style={{ width: "100%", textAlign: "left", padding: "8px 10px", borderRadius: "var(--radius-sm)", background: maxPrice === v ? "var(--brand-light)" : "transparent", border: "none", fontSize: 13, color: maxPrice === v ? "var(--brand)" : "var(--text)", fontWeight: maxPrice === v ? 700 : 400, cursor: "pointer", transition: "background 0.15s" }}>
              {label}
            </button>
          ))}
        </div>
      </Section>

      <Section title="Min Rating">
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {[4, 3, 2].map(r => (
            <button key={r} onClick={() => setParam("minRating", minRating === r ? "" : r)}
              style={{ width: "100%", textAlign: "left", padding: "8px 10px", borderRadius: "var(--radius-sm)", background: minRating === r ? "var(--brand-light)" : "transparent", border: "none", fontSize: 13, color: minRating === r ? "var(--brand)" : "var(--text)", fontWeight: minRating === r ? 700 : 400, cursor: "pointer", display: "flex", alignItems: "center", gap: 6, transition: "background 0.15s" }}>
              <span style={{ display: "flex", gap: 1 }}>
                {[1,2,3,4,5].map(s => <Star key={s} size={11} fill={s <= r ? "#F5A623" : "none"} color={s <= r ? "#F5A623" : "var(--border)"} />)}
              </span>
              & above
            </button>
          ))}
        </div>
      </Section>

      <div style={{ marginBottom: 20 }}>
        <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", padding: "8px 10px", borderRadius: "var(--radius-sm)", background: inStock ? "var(--brand-light)" : "transparent", transition: "background 0.15s" }}>
          <div style={{ width: 18, height: 18, borderRadius: 4, border: `2px solid ${inStock ? "var(--brand)" : "var(--border)"}`, background: inStock ? "var(--brand)" : "#fff", display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.15s", flexShrink: 0 }}>
            {inStock && <span style={{ color: "#fff", fontSize: 11, fontWeight: 700, lineHeight: 1 }}>✓</span>}
          </div>
          <input type="checkbox" checked={inStock} onChange={e => setParam("inStock", e.target.checked ? "true" : "")} style={{ display: "none" }} />
          <span style={{ fontSize: 13, fontWeight: inStock ? 700 : 400, color: inStock ? "var(--brand)" : "var(--text)" }}>In Stock Only</span>
        </label>
      </div>

      <button onClick={clearFilters} className="btn btn-ghost btn-sm w-full" style={{ marginTop: 4, fontSize: 13 }}>
        Clear All Filters
      </button>
    </div>
  );
}

/* ─── Filter Chip ─────────────────────────────────────────────────────────── */
function Chip({ label, onRemove }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 12px", background: "var(--brand-light)", border: "1px solid rgba(232,71,26,0.3)", borderRadius: "var(--radius-full)", fontSize: 13, color: "var(--brand)", fontWeight: 500 }}>
      {label}
      <button onClick={onRemove} style={{ border: "none", background: "transparent", cursor: "pointer", color: "var(--brand)", display: "flex", padding: 0, lineHeight: 1 }}>
        <X size={13} />
      </button>
    </span>
  );
}

/* ─── Skeleton Card ───────────────────────────────────────────────────────── */
function SkeletonCard() {
  return (
    <div style={{ borderRadius: "var(--radius-md)", overflow: "hidden", border: "1px solid var(--border)", background: "#fff" }}>
      <div className="skeleton" style={{ aspectRatio: "4/5" }} />
      <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 9 }}>
        <div className="skeleton" style={{ height: 11, width: "35%" }} />
        <div className="skeleton" style={{ height: 15, width: "95%" }} />
        <div className="skeleton" style={{ height: 13, width: "55%" }} />
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 4 }}>
          <div className="skeleton" style={{ height: 20, width: "40%" }} />
          <div className="skeleton" style={{ height: 34, width: 34, borderRadius: "var(--radius-sm)" }} />
        </div>
      </div>
    </div>
  );
}

/* ─── Main Shop Page ──────────────────────────────────────────────────────── */
export default function ShopPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [allProducts, setAllProducts]   = useState([]);
  const [loading, setLoading]           = useState(true);
  const [drawerOpen, setDrawerOpen]     = useState(false);
  const [page, setPage]                 = useState(1);
  const topRef = useRef(null);

  const rawCategory    = searchParams.get("category") || "";
  const rawSubcategory = searchParams.get("subcategory") || "";
  
  // Resolve category/subcategory
  const resolved = resolveCategory(rawCategory);
  const category = resolved.category || rawCategory;
  const subcategory = rawSubcategory || resolved.subcategory;

  const sort      = searchParams.get("sort") || "newest";
  const maxPrice  = Number(searchParams.get("maxPrice")) || 0;
  const minRating = Number(searchParams.get("minRating")) || 0;
  const inStock   = searchParams.get("inStock") === "true";
  const search    = searchParams.get("search") || "";

  const setParam = (k, v) => {
    const p = new URLSearchParams(searchParams);
    if (v) p.set(k, String(v)); else p.delete(k);
    setSearchParams(p);
    setPage(1);
  };

  const clearFilters = () => { setSearchParams({}); setPage(1); };

  useEffect(() => {
    setLoading(true);
    const q = query(collection(db, "products"), where("status", "==", "active"));
    getDocs(q).then(snap => {
      setAllProducts(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }).catch(err => {
      console.error("Error fetching products:", err);
      setLoading(false);
    });
  }, []);

  // Category items for subcategory pill strip
  const currentCategoryData = CATEGORY_TREE[category];
  const subcategoryList = currentCategoryData?.subcategories || [];

  // Client-side filtering with smart matching for category, subcategory, and legacy categories
  let filtered = allProducts;

  if (category) {
    const validSubNames = subcategoryList.map(s => s.name.toLowerCase());

    filtered = filtered.filter(p => {
      const pCat = (p.category || "").toLowerCase();
      const pSub = (p.subcategory || "").toLowerCase();

      // Legacy fallback mappings
      const legacyMap = {
        "computing": ["laptops & desktops"],
        "electronics": ["led screens & displays", "office electronics", "cables & accessories"],
        "power & solar": ["inverters & batteries", "solar equipment", "lighting & electrical", "solar"],
        "networking": ["starlinks & routers"],
        "home & office": ["household appliances", "household furniture", "office furniture", "home-appliances", "home-interiors"]
      };
      const legList = legacyMap[category.toLowerCase()] || [];

      // Check if product belongs to this main category
      const matchesMain = pCat === category.toLowerCase() ||
                          legList.includes(pCat) ||
                          validSubNames.includes(pCat);

      if (!matchesMain && !validSubNames.includes(pSub)) return false;

      // If a subcategory is also selected, filter further
      if (subcategory) {
        const subLow = subcategory.toLowerCase();
        return pSub === subLow ||
               pCat === subLow ||
               pCat.includes(subLow) ||
               p.name?.toLowerCase().includes(subLow);
      }

      return true;
    });
  } else if (subcategory) {
    const subLow = subcategory.toLowerCase();
    filtered = filtered.filter(p =>
      (p.subcategory || "").toLowerCase() === subLow ||
      (p.category || "").toLowerCase() === subLow
    );
  }

  if (search) {
    filtered = filtered.filter(p =>
      p.name?.toLowerCase().includes(search.toLowerCase()) ||
      p.description?.toLowerCase().includes(search.toLowerCase()) ||
      p.brand?.toLowerCase().includes(search.toLowerCase()) ||
      p.category?.toLowerCase().includes(search.toLowerCase()) ||
      p.subcategory?.toLowerCase().includes(search.toLowerCase()) ||
      (p.tags || []).some(t => t.toLowerCase().includes(search.toLowerCase()))
    );
  }

  if (maxPrice > 0)   filtered = filtered.filter(p => p.price <= maxPrice);
  if (minRating > 0)  filtered = filtered.filter(p => (p.rating || 0) >= minRating);
  if (inStock)        filtered = filtered.filter(p => p.stock > 0);

  if (sort === "newest")     filtered = [...filtered].sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
  if (sort === "price_asc")  filtered = [...filtered].sort((a, b) => a.price - b.price);
  if (sort === "price_desc") filtered = [...filtered].sort((a, b) => b.price - a.price);
  if (sort === "rating")     filtered = [...filtered].sort((a, b) => (b.rating || 0) - (a.rating || 0));
  if (sort === "popular")    filtered = [...filtered].sort((a, b) => (b.reviewCount || 0) - (a.reviewCount || 0));

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paginated  = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const activeFilterCount = [category, subcategory, maxPrice, minRating, inStock, search].filter(Boolean).length;

  function changePage(n) {
    setPage(n);
    topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  return (
    <div style={{ background: "var(--surface)", minHeight: "100vh" }} ref={topRef}>

      {/* ── Page header ───────────────────────────────────────────── */}
      <div style={{ background: "#fff", borderBottom: "1px solid var(--border)", padding: "24px 0" }}>
        <div className="container">
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
            <div>
              <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 4, fontWeight: 500, display: "flex", alignItems: "center", gap: 6 }}>
                <Link to="/" style={{ color: "var(--brand)", textDecoration: "none" }}>Home</Link>
                <span>/</span>
                {category ? (
                  <>
                    <button
                      onClick={() => { setParam("category", category); setParam("subcategory", ""); }}
                      style={{ background: "none", border: "none", padding: 0, color: subcategory ? "var(--brand)" : "var(--text)", cursor: "pointer", fontSize: 12, fontWeight: 500 }}
                    >
                      {category}
                    </button>
                    {subcategory && (
                      <>
                        <span>/</span>
                        <span style={{ color: "var(--text)", fontWeight: 600 }}>{subcategory}</span>
                      </>
                    )}
                  </>
                ) : (
                  <span>All Products</span>
                )}
              </div>

              <h1 style={{ fontFamily: "var(--font-head)", fontSize: "clamp(20px,3vw,28px)", fontWeight: 800, lineHeight: 1.15 }}>
                {search ? `Results for "${search}"` : subcategory ? `${subcategory}` : category || "All Products"}
              </h1>
              <p style={{ fontSize: 13, color: "var(--text-muted)", marginTop: 4 }}>
                {loading ? "Loading products…" : `${filtered.length} product${filtered.length !== 1 ? "s" : ""} found`}
              </p>
            </div>

            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              {/* Filter button (mobile) */}
              <button onClick={() => setDrawerOpen(true)} className="btn btn-ghost btn-sm"
                style={{ display: "flex", gap: 6, position: "relative" }}>
                <SlidersHorizontal size={14} /> Filters
                {activeFilterCount > 0 && (
                  <span style={{ position: "absolute", top: -6, right: -6, background: "var(--brand)", color: "#fff", borderRadius: "50%", width: 18, height: 18, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700 }}>
                    {activeFilterCount}
                  </span>
                )}
              </button>
              {/* Sort */}
              <select className="form-select" style={{ width: "auto", minWidth: 180, fontSize: 13, padding: "8px 36px 8px 12px" }} value={sort} onChange={e => setParam("sort", e.target.value)}>
                <option value="newest">Newest First</option>
                <option value="price_asc">Price: Low → High</option>
                <option value="price_desc">Price: High → Low</option>
                <option value="rating">Top Rated</option>
                <option value="popular">Most Reviewed</option>
              </select>
            </div>
          </div>

          {/* Subcategory Pills Strip (When category is selected) */}
          {category && subcategoryList.length > 0 && (
            <div style={{ marginTop: 20, paddingTop: 16, borderTop: "1px solid var(--border)", display: "flex", alignItems: "center", gap: 8, overflowX: "auto", scrollbarWidth: "none" }}>
              <button
                onClick={() => setParam("subcategory", "")}
                style={{
                  padding: "7px 14px",
                  borderRadius: "var(--radius-full)",
                  fontSize: 13,
                  fontWeight: !subcategory ? 700 : 500,
                  border: !subcategory ? "1.5px solid var(--brand)" : "1px solid var(--border)",
                  background: !subcategory ? "var(--brand-light)" : "#fff",
                  color: !subcategory ? "var(--brand)" : "var(--text)",
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  transition: "all 0.15s"
                }}
              >
                All {category}
              </button>

              {subcategoryList.map(sub => {
                const isActive = subcategory.toLowerCase() === sub.name.toLowerCase();
                return (
                  <button
                    key={sub.name}
                    onClick={() => setParam("subcategory", isActive ? "" : sub.name)}
                    style={{
                      padding: "7px 14px",
                      borderRadius: "var(--radius-full)",
                      fontSize: 13,
                      fontWeight: isActive ? 700 : 500,
                      border: isActive ? "1.5px solid var(--brand)" : "1px solid var(--border)",
                      background: isActive ? "var(--brand-light)" : "#fff",
                      color: isActive ? "var(--brand)" : "var(--text)",
                      cursor: "pointer",
                      whiteSpace: "nowrap",
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      transition: "all 0.15s"
                    }}
                  >
                    <span>{sub.icon}</span>
                    <span>{sub.name}</span>
                  </button>
                );
              })}
            </div>
          )}

          {/* Active filter chips */}
          {activeFilterCount > 0 && (
            <div style={{ display: "flex", gap: 8, marginTop: 16, flexWrap: "wrap", alignItems: "center" }}>
              <span style={{ fontSize: 12, color: "var(--text-muted)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>Active:</span>
              {category && <Chip label={`Category: ${category}`} onRemove={() => { setParam("category", ""); setParam("subcategory", ""); }} />}
              {subcategory && <Chip label={`Subcategory: ${subcategory}`} onRemove={() => setParam("subcategory", "")} />}
              {maxPrice > 0 && <Chip label={`Under ${formatPrice(maxPrice)}`} onRemove={() => setParam("maxPrice", "")} />}
              {minRating > 0 && <Chip label={`${minRating}★ & above`} onRemove={() => setParam("minRating", "")} />}
              {inStock   && <Chip label="In Stock" onRemove={() => setParam("inStock", "")} />}
              {search    && <Chip label={`"${search}"`} onRemove={() => setParam("search", "")} />}
              <button onClick={clearFilters} style={{ fontSize: 12, color: "var(--danger)", border: "none", background: "transparent", cursor: "pointer", fontWeight: 600, padding: "5px 8px" }}>
                Clear all
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ── Content ─────────────────────────────────────────────── */}
      <div className="container" style={{ padding: "clamp(18px, 3vw, 32px) clamp(14px, 3vw, 28px)" }}>
        <SlimAdBanner placement="shop_top" style={{ marginBottom: 20 }} />
        <div style={{ display: "flex", gap: 28, alignItems: "flex-start" }}>

          {/* Desktop Sidebar */}
          <aside style={{ width: 240, flexShrink: 0 }} className="hide-mobile">
            <div style={{ background: "#fff", border: "1px solid var(--border)", borderRadius: "var(--radius-md)", padding: 20, position: "sticky", top: 90, boxShadow: "var(--shadow-xs)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
                <h3 style={{ fontFamily: "var(--font-head)", fontWeight: 800, fontSize: 15 }}>Filters</h3>
                {activeFilterCount > 0 && (
                  <span style={{ background: "var(--brand)", color: "#fff", fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: "var(--radius-full)" }}>
                    {activeFilterCount} active
                  </span>
                )}
              </div>
              <FilterContent searchParams={searchParams} setParam={setParam} clearFilters={clearFilters} />
            </div>
          </aside>

          {/* Product grid */}
          <div style={{ flex: 1, minWidth: 0 }}>
            {loading ? (
              <div className="product-grid">
                {Array(PAGE_SIZE).fill(0).map((_, i) => <SkeletonCard key={i} />)}
              </div>
            ) : paginated.length === 0 ? (
              /* Empty state */
              <div style={{ textAlign: "center", padding: "80px 20px", background: "#fff", borderRadius: "var(--radius-md)", border: "1px solid var(--border)" }}>
                <div style={{ fontSize: 64, marginBottom: 20 }}>🔍</div>
                <h3 style={{ fontFamily: "var(--font-head)", fontSize: 22, fontWeight: 800, marginBottom: 10 }}>No products found</h3>
                <p style={{ fontSize: 15, color: "var(--text-muted)", marginBottom: 28, maxWidth: 380, margin: "0 auto 28px" }}>
                  We couldn't find products matching your filters. Try adjusting or clearing them.
                </p>
                <button onClick={clearFilters} className="btn btn-primary">Clear All Filters</button>
              </div>
            ) : (
              <>
                <div className="product-grid">
                  {paginated.map(p => <ProductCard key={p.id} product={p} />)}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 6, marginTop: 48 }}>
                    <button onClick={() => changePage(page - 1)} disabled={page === 1} className="btn btn-ghost btn-sm">
                      <ChevronLeft size={15} />
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                      .filter(n => n === 1 || n === totalPages || Math.abs(n - page) <= 1)
                      .reduce((acc, n, i, arr) => {
                        if (i > 0 && arr[i - 1] !== n - 1) acc.push("…");
                        acc.push(n);
                        return acc;
                      }, [])
                      .map((n, i) =>
                        n === "…" ? (
                          <span key={`e${i}`} style={{ width: 36, textAlign: "center", color: "var(--text-muted)", fontSize: 14 }}>…</span>
                        ) : (
                          <button key={n} onClick={() => changePage(n)}
                            style={{ width: 38, height: 38, borderRadius: "var(--radius-sm)", border: `1.5px solid ${n === page ? "var(--brand)" : "var(--border)"}`, background: n === page ? "var(--brand)" : "#fff", color: n === page ? "#fff" : "var(--text)", fontWeight: n === page ? 700 : 400, cursor: "pointer", fontSize: 14, transition: "all 0.15s", boxShadow: n === page ? "var(--shadow-brand)" : "none" }}>
                            {n}
                          </button>
                        )
                      )}
                    <button onClick={() => changePage(page + 1)} disabled={page === totalPages} className="btn btn-ghost btn-sm">
                      <ChevronRight size={15} />
                    </button>
                    <span style={{ fontSize: 13, color: "var(--text-muted)", marginLeft: 8 }}>
                      {page} / {totalPages}
                    </span>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* ── Mobile Filter Drawer ───────────────────────────────── */}
      {drawerOpen && (
        <>
          <div className="drawer-overlay" onClick={() => setDrawerOpen(false)} />
          <div className="filter-drawer">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
              <h3 style={{ fontFamily: "var(--font-head)", fontWeight: 800, fontSize: 18 }}>Filters</h3>
              <button onClick={() => setDrawerOpen(false)} style={{ border: "none", background: "var(--surface)", borderRadius: "50%", width: 32, height: 32, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <X size={18} />
              </button>
            </div>
            <FilterContent
              searchParams={searchParams}
              setParam={(k, v) => { setParam(k, v); setDrawerOpen(false); }}
              clearFilters={() => { clearFilters(); setDrawerOpen(false); }}
            />
          </div>
        </>
      )}
    </div>
  );
}
